/**
 * Export Service - Browser-based data export operations
 *
 * This service coordinates with all domain packages to gather trip data and
 * export it using browser download APIs, replacing the backup directory system.
 *
 * Key Responsibilities:
 * - Aggregate data from multiple packages for export
 * - Handle different export scopes (complete trip, selective data)
 * - Generate files in multiple formats (JSON, PDF, ZIP)
 * - Trigger browser downloads with proper file naming
 * - Manage export progress and user feedback
 * - Provide real-time progress tracking
 *
 * Event Integration:
 * - Subscribes to: EXPORT_REQUESTED, DATA_CHANGED
 * - Publishes: EXPORT_STARTED, EXPORT_PROGRESS, EXPORT_COMPLETED, EXPORT_FAILED
 */

import { eventBridgeService } from '@trip-planner-monorepo/shared';
import {
  getTrip,
  getDays,
  getPlaces,
  getAccommodation,
  getTravel
} from '@trip-planner-monorepo/shared';
import { jsonFormatHandler } from '../formats/json';
import { csvFormatHandler } from '../formats/csv';
import { pdfFormatHandler } from '../formats/pdf';

class ExportService {
  constructor() {
    this.isInitialized = false;
    this.activeExports = new Map(); // Track ongoing export operations
    this.formatHandlers = new Map(); // Registry of export format handlers
    this.progressCallbacks = new Map(); // Progress tracking callbacks
  }

  /**
   * Initialize the export service and register event listeners
   * Must be called after all domain packages are initialized
   */
  init() {
    if (this.isInitialized) {
      console.warn('ExportService already initialized');
      return;
    }

    // Register event listeners
    eventBridgeService.subscribe('EXPORT_REQUESTED', this.handleExportRequest.bind(this));
    eventBridgeService.subscribe('BACKUP_SCHEDULED', this.handleScheduledBackup.bind(this));

    // Register default format handlers
    this.registerFormatHandlers();

    this.isInitialized = true;
    console.log('ExportService initialized');
  }

  /**
   * Register format handlers for different export types
   */
  async registerFormatHandlers() {
    this.formatHandlers.set('json', jsonFormatHandler);
    this.formatHandlers.set('csv', csvFormatHandler);
    this.formatHandlers.set('pdf', pdfFormatHandler);

    // For ZIP exports, we'll use JSZip library
    this.formatHandlers.set('zip', {
      process: this.createZipExport.bind(this)
    });
  }

  /**
   * Handle export request from UI or other services
   * @param {Object} event - Export request event with scope and format specifications
   */
  async handleExportRequest(event) {
    const { exportId, userId, tripId, scope, format, options } = event.payload;

    try {
      // Publish export started event
      eventBridgeService.publish('EXPORT_STARTED', {
        exportId,
        userId,
        tripId,
        scope,
        format,
        timestamp: new Date().toISOString()
      });

      // Gather data based on scope
      const data = await this.gatherExportData(userId, tripId, scope);

      // Process with format handler
      const formatHandler = this.formatHandlers.get(format);
      if (!formatHandler) {
        throw new Error(`Unsupported export format: ${format}`);
      }

      const exportResult = await formatHandler.process(data, options);

      // Publish completion event
      eventBridgeService.publish('EXPORT_COMPLETED', {
        exportId,
        userId,
        tripId,
        result: exportResult,
        timestamp: new Date().toISOString()
      });

      return exportResult;

    } catch (error) {
      // Publish failure event
      eventBridgeService.publish('EXPORT_FAILED', {
        exportId,
        userId,
        tripId,
        error: error.message,
        timestamp: new Date().toISOString()
      });

      throw error;
    }
  }

  /**
   * Gather data from all relevant packages based on export scope
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier (null for user-level exports)
   * @param {Object} scope - Export scope specification
   * @returns {Object} Aggregated data for export
   */
  async gatherExportData(userId, tripId, scope) {
    // TODO: Implement data gathering logic
    // This will coordinate with services from:
    // - @trip-planner-monorepo/days
    // - @trip-planner-monorepo/places
    // - @trip-planner-monorepo/accommodation
    // - @trip-planner-monorepo/travel
    // - @trip-planner-monorepo/documents
    // - @trip-planner-monorepo/extras

    const aggregatedData = {
      metadata: {
        exportDate: new Date().toISOString(),
        userId,
        tripId,
        scope,
        version: '1.0.0'
      },
      // Data will be populated by package-specific services
      days: [],
      places: [],
      accommodation: [],
      travel: [],
      documents: [],
      extras: []
    };

    return aggregatedData;
  }

  /**
   * Handle scheduled backup operations
   * @param {Object} event - Backup schedule event
   */
  async handleScheduledBackup(event) {
    // TODO: Implement automated backup logic
    // This will trigger exports for backup purposes
    console.log('Handling scheduled backup:', event);
  }

  /**
   * Get export progress for active operations
   * @param {string} exportId - Export operation identifier
   * @returns {Object} Progress information
   */
  getExportProgress(exportId) {
    return this.activeExports.get(exportId) || null;
  }

  /**
   * Browser-based trip export with download
   * Main method for the new export architecture
   * @param {Object} options - Export configuration
   * @returns {Object} Export result with download information
   */
  async downloadTripExport(options) {
    const {
      tripId,
      userId,
      format = 'json',
      scope = 'complete',
      entityIds = [],
      onProgress = null
    } = options;

    const exportId = `export-${Date.now()}`;

    try {
      // Track progress callback
      if (onProgress) {
        this.progressCallbacks.set(exportId, onProgress);
      }

      // Update progress
      this.updateProgress(exportId, 10, 'Gathering trip data...');

      // Gather data based on scope
      const data = await this.gatherTripData(userId, tripId, scope, entityIds);

      this.updateProgress(exportId, 40, 'Processing data...');

      // Get format handler
      const formatHandler = this.formatHandlers.get(format);
      if (!formatHandler) {
        throw new Error(`Unsupported export format: ${format}`);
      }

      this.updateProgress(exportId, 60, 'Generating export file...');

      // Process data with format handler
      const exportResult = await formatHandler.process(data, {
        format,
        scope,
        onProgress: (progress) => this.updateProgress(exportId, 60 + (progress * 0.3), 'Processing...')
      });

      this.updateProgress(exportId, 90, 'Preparing download...');

      // Generate filename
      const tripName = data.trip?.name || `Trip-${tripId?.substring(0, 8) || 'Unknown'}`;
      const fileName = this.generateFileName(tripName, format);

      // Trigger browser download
      this.triggerBrowserDownload(exportResult.content, fileName, exportResult.mimeType);

      this.updateProgress(exportId, 100, 'Download complete');

      // Cleanup
      this.progressCallbacks.delete(exportId);

      return {
        success: true,
        fileName,
        size: exportResult.size,
        format,
        scope
      };

    } catch (error) {
      this.progressCallbacks.delete(exportId);
      console.error('Export failed:', error);
      throw error;
    }
  }

  /**
   * Gather trip data from all relevant packages
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @param {string|Array} scope - Export scope
   * @param {Array} entityIds - Specific entity IDs for selective export
   * @returns {Object} Aggregated trip data
   */
  async gatherTripData(userId, tripId, scope, entityIds = []) {
    // Determine which data types to include
    const scopeArray = Array.isArray(scope) ? scope :
                      scope === 'complete' ? ['trip', 'days', 'places', 'accommodation', 'travel', 'documents', 'extras'] :
                      [scope];

    const data = {
      metadata: {
        version: '2.0.0',
        exportType: 'trip-download',
        createdAt: new Date().toISOString(),
        tripId,
        userId,
        scope: scopeArray,
        generator: 'Trip Planner Export System'
      }
    };

    // Get trip information if in scope
    if (scopeArray.includes('trip')) {
      data.trip = await this.getTripData(userId, tripId);
    }

    // Get days data if in scope
    if (scopeArray.includes('days')) {
      data.days = await this.getDaysData(userId, tripId, entityIds);
    }

    // Get places data if in scope
    if (scopeArray.includes('places')) {
      data.places = await this.getPlacesData(userId, tripId, entityIds);
    }

    // Get accommodation data if in scope
    if (scopeArray.includes('accommodation')) {
      data.accommodation = await this.getAccommodationData(userId, tripId, entityIds);
    }

    // Get travel data if in scope
    if (scopeArray.includes('travel')) {
      data.travel = await this.getTravelData(userId, tripId, entityIds);
    }

    // Get documents data if in scope
    if (scopeArray.includes('documents')) {
      data.documents = await this.getDocumentsData(userId, tripId, entityIds);
    }

    // Get extras data if in scope
    if (scopeArray.includes('extras')) {
      data.extras = await this.getExtrasData(userId, tripId, entityIds);
    }

    // Get relationships
    data.relationships = await this.getRelationshipData(userId, tripId, scopeArray);

    // Get connection registry links + config, so the JSON export can round-trip
    // through importService without losing item-to-item relationships.
    try {
      const { connectionRegistryService, linkRegistryConfigService } = await import('@trip-planner-monorepo/shared');
      data.links = await connectionRegistryService.getAllLinks(userId, tripId, { includeArchived: true });
      const linkRegistry = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      data._meta = { linkRegistry };
    } catch (linkError) {
      console.warn('Connection registry data not available for export:', linkError.message);
      data.links = [];
    }

    return data;
  }

  /**
   * Create ZIP export with JSON data and media files
   * @param {Object} data - Trip data
   * @param {Object} options - ZIP options
   * @returns {Object} ZIP export result
   */
  async createZipExport(data, options) {
    // This would use JSZip to create a ZIP file
    // For now, return JSON as placeholder
    const jsonResult = await jsonFormatHandler.process(data, options);

    return {
      content: jsonResult.content,
      mimeType: 'application/zip',
      size: jsonResult.size
    };
  }

  /**
   * Generate standardized filename for export
   * @param {string} tripName - Name of the trip
   * @param {string} format - Export format
   * @returns {string} Sanitized filename
   */
  generateFileName(tripName, format) {
    // Sanitize trip name
    const sanitized = tripName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50);

    // Get current date
    const date = new Date().toISOString().split('T')[0];

    // Map format to extension
    const extensions = {
      json: 'json',
      pdf: 'pdf',
      csv: 'csv',
      zip: 'zip'
    };

    return `${sanitized}-${date}.${extensions[format] || 'json'}`;
  }

  /**
   * Trigger browser download for generated file
   * @param {string|Blob} content - File content
   * @param {string} fileName - File name
   * @param {string} mimeType - MIME type
   */
  triggerBrowserDownload(content, fileName, mimeType) {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = fileName;
    downloadLink.style.display = 'none';

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    // Clean up object URL after a delay
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /**
   * Update export progress and notify callbacks
   * @param {string} exportId - Export identifier
   * @param {number} progress - Progress percentage (0-100)
   * @param {string} details - Progress details
   */
  updateProgress(exportId, progress, details) {
    const callback = this.progressCallbacks.get(exportId);
    if (callback) {
      callback({ progress, details });
    }

    // Also publish event for other listeners
    eventBridgeService.publish('EXPORT_PROGRESS', {
      exportId,
      progress,
      details,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Get trip data from Firebase
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @returns {Object} Trip data
   */
  async getTripData(userId, tripId) {
    try {
      const tripData = await getTrip(userId, tripId);
      return tripData || {
        id: tripId,
        name: 'Unknown Trip',
        description: 'Trip data not found'
      };
    } catch (error) {
      console.error('Error fetching trip data:', error);
      throw new Error(`Failed to retrieve trip data: ${error.message}`);
    }
  }

  /**
   * Get days data from days package
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @param {Array} entityIds - Specific day IDs
   * @returns {Array} Days data
   */
  async getDaysData(userId, tripId, entityIds = []) {
    try {
      const allDays = await getDays(userId, tripId);

      // Filter to specific IDs if requested
      if (entityIds.length > 0) {
        return allDays.filter(day => entityIds.includes(day.id));
      }

      return allDays;
    } catch (error) {
      console.error('Error fetching days data:', error);
      return []; // Return empty array on error to allow export to continue
    }
  }

  /**
   * Get places data from places package
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @param {Array} entityIds - Specific place IDs
   * @returns {Array} Places data
   */
  async getPlacesData(userId, tripId, entityIds = []) {
    try {
      const allPlaces = await getPlaces(userId, tripId);

      // Filter to specific IDs if requested
      if (entityIds.length > 0) {
        return allPlaces.filter(place => entityIds.includes(place.id));
      }

      return allPlaces;
    } catch (error) {
      console.error('Error fetching places data:', error);
      return []; // Return empty array on error to allow export to continue
    }
  }

  /**
   * Get accommodation data from accommodation package
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @param {Array} entityIds - Specific accommodation IDs
   * @returns {Array} Accommodation data
   */
  async getAccommodationData(userId, tripId, entityIds = []) {
    try {
      const allAccommodation = await getAccommodation(userId, tripId);

      // Filter to specific IDs if requested
      if (entityIds.length > 0) {
        return allAccommodation.filter(accommodation => entityIds.includes(accommodation.id));
      }

      return allAccommodation;
    } catch (error) {
      console.error('Error fetching accommodation data:', error);
      return []; // Return empty array on error to allow export to continue
    }
  }

  /**
   * Get travel data from travel package
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @param {Array} entityIds - Specific travel IDs
   * @returns {Array} Travel data
   */
  async getTravelData(userId, tripId, entityIds = []) {
    try {
      const allTravel = await getTravel(userId, tripId);

      // Filter to specific IDs if requested
      if (entityIds.length > 0) {
        return allTravel.filter(travel => entityIds.includes(travel.id));
      }

      return allTravel;
    } catch (error) {
      console.error('Error fetching travel data:', error);
      return []; // Return empty array on error to allow export to continue
    }
  }

  /**
   * Get documents data from documents package
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @param {Array} entityIds - Specific document IDs
   * @returns {Array} Documents data
   */
  async getDocumentsData(userId, tripId, entityIds = []) {
    try {
      // TODO: When documents package Firebase integration is complete, implement:
      // const allDocuments = await getDocuments(userId, tripId);
      // For now, return empty array as documents are not yet fully integrated
      console.log('Documents integration pending - returning empty array');
      return [];
    } catch (error) {
      console.error('Error fetching documents data:', error);
      return []; // Return empty array on error to allow export to continue
    }
  }

  /**
   * Get extras data from extras package
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @param {Array} entityIds - Specific extras IDs
   * @returns {Array} Extras data
   */
  async getExtrasData(userId, tripId, entityIds = []) {
    try {
      // TODO: When extras package Firebase integration is complete, implement:
      // const allExtras = await getExtras(userId, tripId);
      // For now, return empty array as extras are not yet fully integrated
      console.log('Extras integration pending - returning empty array');
      return [];
    } catch (error) {
      console.error('Error fetching extras data:', error);
      return []; // Return empty array on error to allow export to continue
    }
  }

  /**
   * Get relationship data between entities
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @param {Array} scope - Data scope
   * @returns {Object} Relationship data
   */
  async getRelationshipData(userId, tripId, scope) {
    try {
      const relationships = {
        dayAccommodationLinks: [],
        placeDayLinks: [],
        documentEntityLinks: [],
        contentBlockLinks: []
      };

      // Extract relationships from data if available
      if (scope.includes('days') && scope.includes('accommodation')) {
        // Get accommodation links from days contentBlocks
        const days = await this.getDaysData(userId, tripId);
        const accommodations = await this.getAccommodationData(userId, tripId);

        days.forEach(day => {
          if (day.contentBlocks) {
            day.contentBlocks.forEach(block => {
              if (block.type === 'accommodation' && block.accommodationId) {
                relationships.dayAccommodationLinks.push({
                  dayId: day.id,
                  accommodationId: block.accommodationId,
                  date: day.date
                });
              }
            });
          }

          // Also check for direct accommodation relationships
          if (day.relatedAccommodations) {
            day.relatedAccommodations.forEach(accId => {
              relationships.dayAccommodationLinks.push({
                dayId: day.id,
                accommodationId: accId,
                date: day.date
              });
            });
          }
        });
      }

      return relationships;
    } catch (error) {
      console.error('Error extracting relationship data:', error);
      return {
        dayAccommodationLinks: [],
        placeDayLinks: [],
        documentEntityLinks: [],
        contentBlockLinks: []
      };
    }
  }

  /**
   * Browser-based user data export with download
   * Exports all user trips and data
   * @param {Object} options - Export configuration
   * @returns {Object} Export result with download information
   */
  async downloadUserDataExport(options) {
    const {
      userId,
      format = 'json',
      onProgress = null
    } = options;

    const exportId = `user-export-${Date.now()}`;

    try {
      // Track progress callback
      if (onProgress) {
        this.progressCallbacks.set(exportId, onProgress);
      }

      this.updateProgress(exportId, 10, 'Gathering user trips...');

      // Import getTrips function
      const { getTrips } = await import('@trip-planner-monorepo/shared');

      // Get all user trips
      const trips = await getTrips(userId);

      this.updateProgress(exportId, 30, 'Gathering data from all trips...');

      // Collect data from all trips
      const userData = {
        metadata: {
          version: '2.0.0',
          exportType: 'user-data-export',
          createdAt: new Date().toISOString(),
          userId,
          generator: 'Trip Planner Export System'
        },
        user: {
          id: userId,
          exportedAt: new Date().toISOString(),
          tripCount: trips.length
        },
        trips: []
      };

      // Process each trip
      for (let i = 0; i < trips.length; i++) {
        const trip = trips[i];
        const progress = 30 + ((i / trips.length) * 50);
        this.updateProgress(exportId, progress, `Processing trip: ${trip.name || trip.id}...`);

        try {
          const tripData = await this.gatherTripData(userId, trip.id, 'complete', []);
          userData.trips.push(tripData);
        } catch (tripError) {
          console.error(`Error processing trip ${trip.id}:`, tripError);
          // Continue with other trips
        }
      }

      this.updateProgress(exportId, 80, 'Generating export file...');

      // Get format handler
      const formatHandler = this.formatHandlers.get(format);
      if (!formatHandler) {
        throw new Error(`Unsupported export format: ${format}`);
      }

      // Process data with format handler
      const exportResult = await formatHandler.process(userData, {
        format,
        scope: 'user',
        onProgress: (progress) => this.updateProgress(exportId, 80 + (progress * 0.15), 'Processing...')
      });

      this.updateProgress(exportId, 95, 'Preparing download...');

      // Generate filename
      const fileName = `user-data-export-${userId.substring(0, 8)}-${new Date().toISOString().split('T')[0]}.${format}`;

      // Trigger browser download
      this.triggerBrowserDownload(exportResult.content, fileName, exportResult.mimeType);

      this.updateProgress(exportId, 100, 'Download complete');

      // Cleanup
      this.progressCallbacks.delete(exportId);

      return {
        success: true,
        fileName,
        size: exportResult.size,
        format,
        scope: 'user',
        tripCount: trips.length
      };

    } catch (error) {
      this.progressCallbacks.delete(exportId);
      console.error('User data export failed:', error);
      throw error;
    }
  }

  /**
   * Cancel an ongoing export operation
   * @param {string} exportId - Export operation identifier
   */
  cancelExport(exportId) {
    const exportInfo = this.activeExports.get(exportId);
    if (exportInfo) {
      exportInfo.cancelled = true;
      this.activeExports.delete(exportId);
      this.progressCallbacks.delete(exportId);

      eventBridgeService.publish('EXPORT_CANCELLED', {
        exportId,
        timestamp: new Date().toISOString()
      });
    }
  }
}

// Create singleton instance
const exportService = new ExportService();

export { exportService };
export default exportService;